import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isPast } from 'date-fns';
import { useMemo } from 'react';

import type { useAuctionConversionCredit_auction } from './__generated__/useAuctionConversionCredit.graphql.ts';
import type { ConversionCreditWithAmounts } from './useConversionCredits.tsx';
import { useMonetaryAmount } from './useMonetaryAmount.ts';

export const useAuctionConversionCredit = (
  auction_?: useAuctionConversionCredit_auction
): ConversionCreditWithAmounts | undefined => {
  const auction = auction_;
  const { toMonetaryAmount } = useMonetaryAmount();

  const conversionCredit = useMemo(() => {
    if (!auction) return undefined;

    const credit = auction.myBestBid?.conversionCredit;
    if (!credit || isPast(credit.endDate)) {
      return undefined;
    }

    const maxDiscount = toMonetaryAmount(credit.maxDiscount);
    const totalDiscount = toMonetaryAmount(credit.totalDiscount);
    return {
      ...credit,
      maxDiscount,
      disabled: false,
      totalDiscount,
    };
  }, [auction, toMonetaryAmount]);

  return conversionCredit as ConversionCreditWithAmounts;
};

useAuctionConversionCredit.fragments = {
  auction: gql`
    fragment useAuctionConversionCredit_auction on TokenAuction {
      id
      myBestBid {
        id
        conversionCredit {
          id
          totalDiscount {
            ...useMonetaryAmount_monetaryAmount
          }
          maxDiscount {
            ...useMonetaryAmount_monetaryAmount
          }
          percentageDiscount
          endDate
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useAuctionConversionCredit_auction>,
};
